import { useMemo, useRef, useState } from 'react';
import { useLoaderData } from 'react-router';
import { useTracker } from '@archipro-website/tracker';

import { cn } from '@rocco/utils/cn';

import type {
    ContactLoaderData,
    LocationDataType,
} from '@modules/professional/type/contact-page-types';
import { useProfessionalInitialRouteData } from '@modules/professional/hook/use-professional-initial-route-data';
import { useContactLocationMap } from '@modules/professional/hook/use-contact-location-map';
import { getLinkedProfileLocations } from '@modules/professional/util/contact-page-helper';
import { LocationMap } from '@modules/professional/component/contact-tab/LocationMap';
import { EnquiryInline } from '~/modules/enquiry/component/enquiry-inline/EnquiryInline';
import { usePageCtas } from '~/modules/enquiry/component/PageCtasProvider';
import { SpecifierLoginPrompt } from '~/modules/enquiry/component/specifier-login-prompt/SpecifierLoginPrompt';
import { CONTACT_ENQUIRY_ANCHOR } from './ProfessionalContactConnectSection';
import {
    ContactMobileLocationList,
    ContactMobileViewToggle,
    type ContactMobileView,
} from './ProfessionalContactMobileLocations';
import { ProfessionalLocationCard } from './ProfessionalLocationCard';

/**
 * Contact tab location section (Figma 2193:11424) — the editorial map +
 * enquiry block. Left: the live Google map (reused {@link LocationMap}, real
 * pins) with a slim primary-location card overlaid; right: the inline enquiry
 * form in a light panel. Mobile swaps the overlay for a List / Map switch
 * that opens on every branch as a card. The form is cosmetically matched to the Figma
 * (progress bar hidden, Send restyled as a solid pill) via CSS scoped to
 * `.contact-enquiry-form` so the shared EnquiryForm is unchanged elsewhere.
 */
export const ProfessionalContactLocationSection = () => {
    const loaderData = useLoaderData<ContactLoaderData>();
    const linkedProfiles =
        loaderData?.linkedProfiles as ContactLoaderData['linkedProfiles'];
    const professional = useProfessionalInitialRouteData();
    // The inline form is the page's Enquire button in another shape, so it
    // follows that button's visibility.
    const {
        ctas: { enquire },
    } = usePageCtas();

    const originalLocations = useMemo(
        () =>
            getLinkedProfileLocations(
                professional,
                linkedProfiles ?? {
                    linkedProfilesCount: 0,
                    linkedProfilesList: [],
                    linkedProfilesProductSharing: [],
                },
            ),
        [professional, linkedProfiles],
    );

    const {
        mapCenter,
        mapZoomNum,
        mapVariables,
        onMapMarkerHover,
        onMapMarkerClick,
        onLocationDetailClose,
        onListItemClick,
        hoveredLocation,
        clickedLocation,
        filteredLocations,
        sortedLocations,
    } = useContactLocationMap(
        originalLocations,
        undefined,
        true,
        professional.Country,
        // Open zoomed out so all branches show; zoom in only when a pin is
        // clicked (matches the contact-tab feedback).
        false,
    );

    // The card follows the clicked pin (prod behaviour), defaulting to the
    // head office.
    const selected = clickedLocation ?? sortedLocations[0];
    const hasLocations = sortedLocations.length > 0;
    // Mobile opens on the list, as the legacy page does: a 24rem map has no
    // room for a card over it, and a pin alone tells the reader nothing.
    const [mobileView, setMobileView] = useState<ContactMobileView>('list');
    const mobileTopRef = useRef<HTMLDivElement>(null);

    // A pin pressed on the map, desktop or the phone's Map view: the event the
    // legacy contact page sent for it.
    const tracker = useTracker();
    const onPinClick = (location: LocationDataType) => {
        void tracker.log('ProfessionalMapPinClick', {
            url: new URL(window.location.href),
            targetTracker: 'archiproTracker',
        });
        onMapMarkerClick(location);
    };

    // "View on map" is pressed from anywhere down the list; bring the reader
    // back up to the map it opens.
    const showOnMap = (location: LocationDataType) => {
        onListItemClick(location);
        setMobileView('map');
        mobileTopRef.current?.scrollIntoView({ block: 'start' });
    };

    return (
        <section className="w-full overflow-hidden bg-ground text-[#1f1f21] max-md:px-6 max-md:pt-8 max-md:pb-12 md:px-[3.125rem] md:pt-[1.5rem] md:pb-[7.5rem]">
            <div className="mx-auto w-full max-w-[113.75rem]">
                <div className="flex max-md:flex-col max-md:gap-8 md:gap-[8.2%]">
                    {hasLocations ? (
                        <div
                            ref={mobileTopRef}
                            className="flex scroll-mt-40 flex-col gap-4 md:hidden"
                        >
                            <ContactMobileViewToggle
                                view={mobileView}
                                onChange={setMobileView}
                                locationCount={sortedLocations.length}
                            />
                            {mobileView === 'list' ? (
                                <ContactMobileLocationList
                                    locations={sortedLocations}
                                    professionalId={professional.ID}
                                    professionalTitle={professional.Title}
                                    onViewOnMap={showOnMap}
                                />
                            ) : null}
                        </div>
                    ) : null}

                    {/* Map, with the selected location's card overlaid on
                    desktop. On mobile the map only shows in the Map view and
                    its card sits underneath, outside the clipped frame. */}
                    <div
                        className={cn(
                            'bg-neutral-800 relative min-w-0 overflow-hidden rounded-tile max-md:h-[24rem] md:h-[42.8125rem] md:w-[57.9%]',
                            (!hasLocations || mobileView === 'list') &&
                                'max-md:hidden',
                        )}
                    >
                        {hasLocations ? (
                            <LocationMap
                                center={mapCenter}
                                zoom={mapZoomNum}
                                filteredLocations={filteredLocations}
                                onMapMarkerHover={onMapMarkerHover}
                                onMapMarkerClick={onPinClick}
                                onMapPopupClose={onLocationDetailClose}
                                hoveredLocation={hoveredLocation}
                                clickedLocation={clickedLocation}
                                linkedProfiles={linkedProfiles}
                                mapVariables={mapVariables}
                                showMobilePopup={false}
                            />
                        ) : null}

                        {selected ? (
                            <ProfessionalLocationCard
                                location={selected}
                                professionalId={professional.ID}
                                title={professional.Title}
                                isHeadOffice={
                                    selected.ID === sortedLocations[0]?.ID
                                }
                                className="max-md:hidden md:absolute md:top-[1.75rem] md:left-[1.625rem] md:w-[27.375rem]"
                            />
                        ) : null}
                    </div>

                    {selected && mobileView === 'map' ? (
                        <ProfessionalLocationCard
                            location={selected}
                            professionalId={professional.ID}
                            title={selected.Title || professional.Title}
                            isHeadOffice={
                                selected.ID === sortedLocations[0]?.ID
                            }
                            className="max-md:-mt-4 md:hidden"
                        />
                    ) : null}

                    {/* Inline enquiry form — stretches to the map's height so the
                    two columns align (Figma). */}
                    <div
                        id={CONTACT_ENQUIRY_ANCHOR}
                        // White, not an off-white: on the profile's #f5f5f7 the
                        // panel's edge was invisible and the form read as loose
                        // fields on the page rather than a box to fill in.
                        className="rounded-[0.75rem] bg-white max-md:w-full max-md:p-6 md:w-[28.55%] md:self-stretch md:px-[2.6875rem] md:py-[3.125rem]"
                    >
                        {enquire.showSpecifierLoginPrompt ? (
                            <SpecifierLoginPrompt
                                professionalName={professional.Title}
                                itemDescription="this professional"
                                showBottomBorder={false}
                            />
                        ) : enquire.visible ? (
                            <div className="contact-enquiry-form">
                                <EnquiryInline
                                    professional={professional}
                                    includeBranch
                                    // The revamp does not ask for a suburb —
                                    // the reader's home address is not where
                                    // the project is, and an attached BuildHub
                                    // project carries the real location.
                                    collectLocation={false}
                                    // Same attachment pills as the enquiry
                                    // modal, so an attached file reads the same
                                    // wherever the reader enquires from.
                                    attachmentVariant="compact"
                                    showCustomEnquiryText
                                    submitButtonText="Send"
                                    siteTreeID={professional.ID}
                                    itemType="professional"
                                    itemTitle={professional.Title}
                                    ctaLabel="Get in touch"
                                />
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        </section>
    );
};
